import React, { useCallback, useEffect, useRef, useState } from 'react';
import enrichmentApi from '../services/enrichmentApi';
import Icon from './Icon';
import { timeAgo, timeUntil } from '../utils/time';
import './ResearchButton.css';

const POLL_MS = 3000;
const KIND = 'research';

const summary = (before, findings, lastTask) => {
  const fresh = findings.filter(finding => !before.has(finding.id));
  const filled = fresh.filter(finding => finding.status === 'applied').length;
  const waiting = fresh.filter(finding => finding.status === 'proposed').length;
  if (!filled && !waiting) {
    if (lastTask?.status === 'failed') return `The agent couldn't run: ${lastTask.note || 'unknown error'}.`;
    return 'The agent found nothing it could show a source for.';
  }
  return [filled ? `Filled in ${filled}` : null, waiting ? `${waiting} waiting for review` : null]
    .filter(Boolean).join(', ') + '.';
};

/** Sends an agent to look for what's missing about one person or company. */
const ResearchButton = ({ subjectType, subjectId, onFinished }) => {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [nextDue, setNextDue] = useState(null);
  const known = useRef(new Set());
  const timer = useRef(null);

  const loadLastRun = useCallback(() => {
    enrichmentApi.subject(subjectType, subjectId, KIND)
      .then(state => { setLastRun(state.last_task); setNextDue(state.next_due); })
      .catch(() => {
        // Non-critical: the button still works without a "Last run"/"Next run" line
      });
  }, [subjectType, subjectId]);

  const watch = useCallback(async () => {
    try {
      const state = await enrichmentApi.subject(subjectType, subjectId);
      if (state.active) {
        timer.current = setTimeout(watch, POLL_MS);
        return;
      }
      setActive(false);
      setMessage(summary(known.current, state.findings, state.last_task));
      loadLastRun();
      onFinished?.();
    } catch (err) {
      setActive(false);
      setMessage(err.message);
    }
  }, [subjectType, subjectId, onFinished, loadLastRun]);

  useEffect(() => {
    setActive(false);
    setMessage(null);
    setLastRun(null);
    setNextDue(null);
    loadLastRun();
    return () => clearTimeout(timer.current);
  }, [subjectType, subjectId, loadLastRun]);

  const start = async () => {
    setMessage(null);
    setActive(true);
    try {
      const state = await enrichmentApi.subject(subjectType, subjectId);
      known.current = new Set(state.findings.map(finding => finding.id));
      await enrichmentApi.startRun(subjectType, { ids: [subjectId] });
      timer.current = setTimeout(watch, POLL_MS);
    } catch (err) {
      setActive(false);
      setMessage(err.message);
    }
  };

  return (
    <div className="research-button">
      <button type="button" className="rm-button" onClick={start} disabled={active}>
        <Icon name={active ? 'loader' : 'search'} spin={active} />
        {active ? 'Researching...' : 'Find what’s missing'}
      </button>
      {message
        ? <p role="status">{message}</p>
        : <>
            {lastRun?.finished_at && <p className="research-button__last-run">Last run: {timeAgo(lastRun.finished_at)}</p>}
            {nextDue && (
              <p className="research-button__next-run">
                Next run: {nextDue.overdue ? 'due now' : timeUntil(nextDue.due_at)}
              </p>
            )}
          </>}
    </div>
  );
};

export default ResearchButton;
